import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { SchedulerDialogData, SchedulerFormValue, SchedulerValue } from './scheduler.interface';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import timeGridPlugin from '@fullcalendar/timegrid';
import { CalendarOptions } from '@fullcalendar/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SchedulerService } from './scheduler.service';
import moment from 'moment';
import { AppService } from '../../app.service';

@Component({
  selector: 'app-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrls: ['./scheduler.component.scss']
})
export class SchedulerComponent implements OnInit, AfterViewInit {
  form: SchedulerFormValue = this.schedulerService.createDefaultFormValue();

  readonly frequencyOptions = this.schedulerService.frequencyOptions;
  readonly weekdays = this.schedulerService.weekdays;
  readonly monthlyPositions = this.schedulerService.monthlyPositions;
  readonly monthDays = this.schedulerService.monthDays;
  readonly months = this.schedulerService.months;

  calendar_options: CalendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    height: 'auto',
    editable: false,
    selectable: false,
    eventStartEditable: false,
    eventDurationEditable: false,
    events: []
  };

  scheduleStartDateTime: moment.Moment | null;
  scheduleEndDateTime: moment.Moment | null;
  today: moment.Moment;

  timezone: string;

  constructor(
    private dialogRef: MatDialogRef<SchedulerComponent, SchedulerValue>,
    @Inject(MAT_DIALOG_DATA)
    public data: SchedulerDialogData,
    public schedulerService: SchedulerService,
    private app: AppService
  ) {}

  ngOnInit(): void {
    this.timezone = this.data?.timezone ?? this.app.account.timezone;

    this.today = moment()
      .tz(this.timezone)
      .startOf('day');

    this.calendar_options = {
      ...this.calendar_options,
      timeZone: this.timezone
    };

    this.form = this.schedulerService.toSchedulerFormValue(this.data?.value || null);

    this.loadDateTimePickers();

    this.applyDefaultsForFrequency();

    this.setInitialCalendarDate();

    this.refreshCalendar();
  }

  /**
   * Refreshes the FullCalendar preview after the dialog layout has finished
   * settling so that the calendar renders at the correct width.
   *
   * @return {void}
   */
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.refreshCalendar();
    }, 200);
  }

  /**
   * Retrieves a summary of the scheduler based on the current form data and timezone.
   *
   * @return {string} The generated summary of the scheduler.
   */
  get schedulerSummary(): string {
    return this.schedulerService.getSummary(this.form, this.timezone);
  }

  /**
   * Determines whether the current state allows saving.
   *
   * @return {boolean} True if the current state is valid and saving is allowed; otherwise, false.
   */
  get canSave(): boolean {
    return this.isValid();
  }

  /**
   * Updates and manages schedule start date-time details.
   *
   * @param {moment.Moment | null} dateTime - The new start date-time for the schedule. If null, the start date-time is cleared.
   * @return {void}
   */
  getOutputScheduleStartDateTimeDetails(dateTime: moment.Moment | null): void {
    this.scheduleStartDateTime = dateTime;
    this.form.schedule_start_at = this.getScheduleTimestamp(this.scheduleStartDateTime, 'start');

    // If Start is moved beyond the selected End, clear the End.
    if (
      this.scheduleStartDateTime &&
      this.scheduleEndDateTime &&
      this.scheduleEndDateTime.isBefore(this.scheduleStartDateTime)
    ) {
      this.scheduleEndDateTime = null;
      this.form.schedule_end_at = null;
    }

    this.applyDateBasedDefaults();

    this.refreshCalendar();
  }

  /**
   * Updates the output schedule's end date and time details.
   *
   * @param {moment.Moment|null} dateTime - The moment date-time object representing the new end date and time, or null to clear it.
   * @return {void}
   */
  getOutputScheduleEndDateTimeDetails(dateTime: moment.Moment | null): void {
    this.scheduleEndDateTime = dateTime;
    this.form.schedule_end_at = this.getScheduleTimestamp(this.scheduleEndDateTime, 'end');

    this.refreshCalendar();
  }

  /**
   * Handles the change in the "All Day" status of a scheduled event.
   * Recalculates the stored Scheduler timestamps based on the current
   * schedule start and end DateTime while preserving the original user-selected
   * times if the "All Day" option is toggled off again. Updates the form fields
   * with recalculated timestamps and refreshes the calendar display accordingly.
   *
   * @return {void}
   */
  onAllDayChange(): void {
    this.form.schedule_start_at = this.getScheduleTimestamp(this.scheduleStartDateTime, 'start');
    this.form.schedule_end_at = this.getScheduleTimestamp(this.scheduleEndDateTime, 'end');

    this.refreshCalendar();
  }

  /**
   * Handles the change event for frequency and updates the UI accordingly.
   * Applies default settings based on the new frequency value
   * and refreshes the associated calendar view.
   *
   * @return {void}
   */
  onFrequencyChange(): void {
    this.applyDefaultsForFrequency();

    this.refreshCalendar();
  }

  /**
   * Handles the event triggered upon changing to the monthly mode in the application.
   * The method applies default settings specific to the monthly mode and refreshes the calendar view.
   *
   * @return {void}
   */
  onMonthlyModeChange(): void {
    this.applyMonthlyDefaults();

    this.refreshCalendar();
  }

  /**
   * Handles changes to the schedule by triggering a calendar refresh.
   *
   * @return {void}
   */
  onScheduleChange(): void {
    this.refreshCalendar();
  }

  /**
   * Toggles the presence of a weekday in the list of selected weekdays.
   *
   * @param {string} key - The name of the weekday to be toggled.
   * @return {void}
   */
  toggleWeekday(key: string): void {
    const index = this.form.weekdays.indexOf(key);

    if (index >= 0) {
      this.form.weekdays.splice(index, 1);
    } else {
      this.form.weekdays.push(key);
    }

    this.refreshCalendar();
  }

  /**
   * Determines if a specified weekday is selected.
   *
   * @param {string} key - The key representing the weekday to check.
   * @return {boolean} True if the specified weekday is selected, otherwise false.
   */
  isWeekdaySelected(key: string): boolean {
    return this.form.weekdays.includes(key);
  }

  /**
   * Saves the current state of the form if it is valid. Converts the form data into a scheduler-compatible format
   * and closes the dialog with the processed value.
   *
   * @return {void}
   */
  onSave(): void {
    if (!this.isValid()) {
      return;
    }

    const value = this.schedulerService.toSchedulerValue(this.form);

    this.dialogRef.close(value);
  }

  /**
   * Cancels the current operation and closes the dialog.
   *
   * @return {void}
   */
  onCancel(): void {
    this.onClose();
  }

  /**
   * Handles the logic for closing the dialog.
   *
   * @return {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Initializes the date-time pickers for the schedule start and end fields.
   * Parses the provided UNIX timestamps from the form, applies the timezone,
   * and assigns the resulting moment objects to the corresponding properties.
   *
   * @return {void}
   */
  private loadDateTimePickers(): void {
    this.scheduleStartDateTime = this.form.schedule_start_at
      ? moment
        .unix(this.form.schedule_start_at)
        .tz(this.timezone)
      : null;

    this.scheduleEndDateTime = this.form.schedule_end_at
      ? moment
        .unix(this.form.schedule_end_at)
        .tz(this.timezone)
      : null;
  }

  /**
   * Calculates and returns a schedule timestamp in Unix format based on a given dateTime and boundary type.
   *
   * @param {moment.Moment | null} dateTime - The moment object representing the date and time, or null if not applicable.
   * @param {'start' | 'end'} boundary - Specifies whether the timestamp should align with the start or end of the day.
   * @return {number | null} The Unix timestamp in seconds for the specified dateTime and boundary, or null if the input is invalid.
   */
  private getScheduleTimestamp(dateTime: moment.Moment | null, boundary: 'start' | 'end'): number | null {
    if (!moment.isMoment(dateTime)) {
      return null;
    }

    const value: moment.Moment = moment.tz(
      dateTime.format('M/D/YYYY, h:mm:ss a'),
      'M/D/YYYY, h:mm:ss a',
      this.timezone
    );

    if (this.form.is_all_day) {
      if (boundary === 'start') {
        value.startOf('day');
      } else {
        value.endOf('day');
      }
    }

    return value.unix();
  }

  /**
   * Refreshes the calendar by updating its configuration and associated events.
   * Clears the events if the preview is not valid; otherwise, it creates and adds
   * a preview event based on the provided form data.
   *
   * @return {void}
   */
  private refreshCalendar(): void {
    if (!this.isPreviewValid()) {
      this.calendar_options = {
        ...this.calendar_options,
        events: []
      };

      return;
    }

    const event = this.schedulerService.buildPreviewEvent(
      this.form,
      this.timezone,
      this.data?.title || 'Scheduled Event'
    );

    this.calendar_options = {
      ...this.calendar_options,
      events: event ? [event] : []
    }
  }

  /**
   * Validates the preview configuration based on various form conditions.
   *
   * The method verifies if the necessary properties in the form object are provided and meet specific criteria.
   * It checks for required fields like start date, time, interval, and ensures correct setup depending on the selected frequency (e.g., weekly or monthly settings).
   *
   * @return {boolean} Returns true if the preview configuration satisfies all the validation rules; otherwise, returns false.
   */
  private isPreviewValid(): boolean {
    if (!this.form.schedule_start_at) {
      return false;
    }

    const usesSpecificMonths = this.form.frequency === 'monthly' && this.form.monthly_mode === 'multiple_days_months';

    if (
      !usesSpecificMonths &&
      (!this.form.interval || this.form.interval < 1)
    ) {
      return false;
    }

    if (
      this.form.frequency === 'weekly' &&
      !this.form.weekdays.length
    ) {
      return false;
    }

    if (
      this.form.frequency === 'monthly' &&
      this.form.monthly_mode === 'day_of_month' &&
      (!this.form.monthly_day || this.form.monthly_day < 1 || this.form.monthly_day > 31)
    ) {
      return false;
    }

    if (
      this.form.frequency === 'monthly' &&
      this.form.monthly_mode === 'multiple_days_months' &&
      (!this.form.monthly_days.length || !this.form.monthly_months.length)
    ) {
      return false;
    }

    if (
      this.form.frequency === 'monthly' &&
      this.form.monthly_mode === 'multiple_days' &&
      !this.form.monthly_days.length
    ) {
      return false;
    }

    if (
      this.form.frequency === 'monthly' &&
      this.form.monthly_mode === 'weekday' &&
      (!this.form.monthly_weekday || !this.form.monthly_position)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Determines whether the current state is valid by checking preview validity
   * and ensuring that required form properties are properly set.
   *
   * @return {boolean} Returns true if the state is valid; otherwise, false.
   */
  private isValid(): boolean {
    if (!this.isPreviewValid()) {
      return false;
    }

    if (
      this.form.schedule_start_at &&
      this.form.schedule_end_at &&
      this.form.schedule_end_at <
      this.form.schedule_start_at
    ) {
      return false;
    }

    if (this.form.frequency === 'weekly' && !this.form.weekdays.length) {
      return false;
    }

    return true;
  }

  /**
   * Sets the initial date for the calendar based on the `scheduleStartDateTime`.
   * If `scheduleStartDateTime` is not defined, the method returns without making changes.
   *
   * @return {void}
   */
  private setInitialCalendarDate(): void {
    if ( !moment.isMoment(this.scheduleStartDateTime) ) {
      return;
    }

    this.calendar_options = {
      ...this.calendar_options,
      initialDate: this.scheduleStartDateTime.format('YYYY-MM-DD')
    };
  }

  /**
   * Applies default values to the form based on the `scheduleStartDateTime`
   * and the selected frequency. If the `scheduleStartDateTime` is not defined,
   * the method exits without applying defaults. For a weekly frequency, it sets
   * the weekdays based on the start date if no weekdays are specified. For a
   * monthly frequency, it calls another method to apply specific monthly defaults.
   *
   * @return {void}
   */
  private applyDateBasedDefaults(): void {
    if (!this.scheduleStartDateTime) {
      return;
    }

    if (this.form.frequency === 'weekly' && !this.form.weekdays.length) {
      this.form.weekdays = [this.getWeekdayFromStartDate()];
    }

    if (this.form.frequency === 'monthly') {
      this.applyMonthlyDefaults();
    }
  }

  /**
   * Applies default values to the form based on the specified frequency type.
   * For "weekly" frequency, sets the default weekdays if none are provided.
   * For "monthly" frequency, delegates to another method to apply the defaults.
   *
   * @return {void}
   */
  private applyDefaultsForFrequency(): void {
    if (this.form.frequency === 'weekly' && !this.form.weekdays.length) {
      this.form.weekdays = [this.getWeekdayFromStartDate()];
    }

    if (this.form.frequency === 'monthly') {
      this.applyMonthlyDefaults();
    }
  }

  /**
   * Applies default values for monthly settings in the form based on the selected mode.
   *
   * @return {void}
   */
  private applyMonthlyDefaults(): void {
    if (this.form.monthly_mode === 'day_of_month') {
      if (!this.form.monthly_day) {
        this.form.monthly_day = this.getDayOfMonthFromStartDate();
      }

      return;
    }

    if (this.form.monthly_mode === 'weekday') {
      if (!this.form.monthly_weekday) {
        this.form.monthly_weekday = this.getWeekdayFromStartDate();
      }

      if (!this.form.monthly_position) {
        this.form.monthly_position = this.getMonthlyPositionFromStartDate();
      }
    }
  }

  /**
   * Retrieves the day of the month from the `start_date` property within the form.
   * If `start_date` is not provided, defaults to 1.
   *
   * @return {number} The day of the month extracted from the `start_date`. Defaults to 1 if `start_date` is not set.
   */
  private getDayOfMonthFromStartDate(): number {
    if (!this.scheduleStartDateTime) {
      return 1;
    }

    return this.scheduleStartDateTime.date();
  }

  /**
   * Calculates the weekday abbreviation based on the provided start date in the form.
   * If no start date is found, it defaults to 'MO'.
   *
   * @return {string} The weekday abbreviation (e.g., 'MO', 'TU', 'WE', etc.) corresponding to the start date.
   */
  private getWeekdayFromStartDate(): string {
    if (!this.scheduleStartDateTime) {
      return 'MO';
    }

    const keys = [
      'SU',
      'MO',
      'TU',
      'WE',
      'TH',
      'FR',
      'SA'
    ];

    return keys[this.scheduleStartDateTime.day()];
  }

  /**
   * Calculates the weekly position of a given start date within the month.
   * If the start date is not defined, it defaults to the first position.
   * If the calculated position is greater than or equal to 5, it returns -1.
   *
   * @return {number} The weekly position of the start date in the month, or -1 if the position is greater than or equal to 5.
   */
  private getMonthlyPositionFromStartDate(): number {
    if (!this.scheduleStartDateTime) {
      return 1;
    }

    const position = Math.ceil(this.scheduleStartDateTime.date() / 7);

    return position >= 5
      ? -1
      : position;
  }

  /**
   * Determines whether the selected monthly pattern contains a day that may
   * be skipped in shorter months.
   *
   * Applies to Day of Month, Multiple Days, and Multiple Days and Months.
   *
   * @return {boolean} True if any selected day is between the 29th and 31st; otherwise false.
   */
  hasShortMonthWarning(): boolean {
    if (this.form.frequency !== 'monthly') {
      return false;
    }

    if (this.form.monthly_mode === 'day_of_month') {
      return (
        this.form.monthly_day !== null &&
        this.form.monthly_day >= 29
      );
    }

    if (
      this.form.monthly_mode === 'multiple_days' ||
      this.form.monthly_mode === 'multiple_days_months'
    ) {
      return this.form.monthly_days.some(
        day => day >= 29
      );
    }

    return false;
  }

  /**
   * Handles the duration change event by recalculating the duration in minutes
   * based on the specified value and unit. Updates the form's duration in minutes
   * and triggers a calendar refresh.
   *
   * @return {void}
   */
  onDurationChange(): void {
    this.form.duration_minutes = this.schedulerService.calculateDurationMinutes(
      this.form.duration_value,
      this.form.duration_unit
    );

    this.refreshCalendar();
  }

  /**
   * Handles the duration value by validating and rounding the value to the
   * nearest whole number. When called from the input blur event, the displayed
   * value is also normalized to the rounded integer.
   *
   * @param {FocusEvent|null} event The optional input blur event.
   * @return {void}
   */
  onDurationValueBlur(event: FocusEvent | null = null): void {
    const value = this.form.duration_value;

    if (value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return;
    }

    const roundedValue = Math.max(1, Math.round(numericValue));

    this.form.duration_value = roundedValue;

    if ( event?.target ) {
      (event.target as HTMLInputElement).value = roundedValue.toString();
    }

    this.onDurationChange();
  }

  /**
   * Handles the repeat interval by validating and rounding the value to the
   * nearest whole number. When called from the input blur event, the displayed
   * value is also normalized to the rounded integer.
   *
   * @param {FocusEvent|null} event The optional input blur event.
   * @return {void}
   */
  onIntervalValueBlur(event: FocusEvent | null = null): void {
    const value = this.form.interval;

    if (value === null || value === undefined) {
      return;
    }

    const numericValue = Number(value);

    if (isNaN(numericValue)) {
      return;
    }

    const roundedValue = Math.max(1, Math.round(numericValue));

    this.form.interval = roundedValue;

    if ( event?.target ) {
      (event.target as HTMLInputElement).value = roundedValue.toString();
    }

    this.onScheduleChange();
  }
}
